import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'

const FIVE_MINUTES = 5 * 60 * 1000
const GET_USER_BY_EMAIL = gql`
  query GetUserEmail($email: String!) {
    getUserByEmail(email: $email) {
      id
      name
      firstName
      lastName
      phoneNumber
      email
      mobileVerificationEligible
      mobileVerified
    }
  }
`

export type UserProfile = {
  id: string
  name: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  mobileVerificationEligible: boolean
  mobileVerified: boolean
}

/**
 * Hook to fetch a user by email.
 *
 * @param email - The email address to query.
 * @returns Query result containing user data.
 */
export const useGetUserApi = (
  email: string,
): UseQueryResult<UserProfile, Error> => {
  const client = useGraphQLClient()

  const fetchUserByEmail = async (email: string): Promise<UserProfile> => {
    if (!email) {
      throw new Error('Email is required to fetch user')
    }

    const data = await client.request<{ getUserByEmail: UserProfile }>(
      GET_USER_BY_EMAIL,
      {
        email,
      },
    )

    return data.getUserByEmail
  }

  return useQuery<UserProfile, Error>({
    queryKey: ['getUserByEmail', email],
    queryFn: () => fetchUserByEmail(email),
    enabled: !!email,
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES,
  })
}
